import { useCallback } from 'react';
import { BookResult } from '../types';

type ConfidenceColor = 'success' | 'warning' | 'error';

/**
 * Custom hook for confidence-related formatting and calculations.
 *
 * Centralizes the logic for determining match strength and display colors.
 */
export function useConfidenceFormatting() {
  /**
   * Format confidence as a percentage string.
   */
  const formatConfidence = useCallback((confidence: number): string => {
    return `${Math.round(confidence * 100)}%`;
  }, []);

  /**
   * Determine if a book result is a "strong match".
   *
   * Takes into account:
   * - AI verification status
   * - Pending SKU status
   * - Confidence thresholds
   */
  const isStrongMatch = useCallback((book: BookResult): boolean => {
    // Pending SKUs are not strong matches
    if (book.is_pending) {
      return false;
    }

    // Check if AI verification was applied
    const wasAiVerified = book.llm_verification?.applied === true;

    // Use different thresholds based on verification method
    const confidenceThreshold = wasAiVerified ? 0.8 : 0.90;

    // Check confidence level
    const hasHighConfidence = book.confidence >= confidenceThreshold;

    // Check if AI verification found a match
    const aiVerified = !(book.no_reliable_match === true);

    return hasHighConfidence && aiVerified;
  }, []);

  /**
   * Get the color for a confidence value.
   *
   * @param confidence - The confidence value (0-1)
   * @param book - Optional book result for additional context
   */
  const getConfidenceColor = useCallback(
    (confidence: number, book?: BookResult): ConfidenceColor => {
      // Handle error state
      if (book?.isbn === 'error') return 'warning';

      // Handle pending SKU state
      if (book?.is_pending) return 'warning';

      // Check if AI verification was applied for threshold
      if (book?.llm_verification?.applied === true) {
        if (confidence >= 0.8) return 'success';
      } else {
        if (confidence >= 0.90) return 'success';
      }

      // Warning/error thresholds
      if (confidence >= 0.5) return 'warning';
      return 'error';
    },
    []
  );

  /**
   * Get background color for a book card based on match status.
   *
   * Returns theme palette keys.
   */
  const getCardBackgroundKey = useCallback(
    (book: BookResult): 'success.light' | 'warning.light' | 'error.light' => {
      if (book.isbn === 'error') {
        return 'warning.light';
      }
      if (book.is_pending) {
        return 'warning.light';
      }
      return isStrongMatch(book) ? 'success.light' : 'error.light';
    },
    [isStrongMatch]
  );

  /**
   * Get a human-readable match status description.
   */
  const getMatchStatusDescription = useCallback((book: BookResult): string => {
    if (book.is_pending) {
      return 'Processing...';
    }
    if (book.isbn === 'error') {
      return 'Error occurred';
    }
    if (book.no_reliable_match) {
      return 'No reliable match found';
    }
    if (isStrongMatch(book)) {
      return 'Strong match';
    }
    return 'Weak match';
  }, [isStrongMatch]);

  return {
    formatConfidence,
    isStrongMatch,
    getConfidenceColor,
    getCardBackgroundKey,
    getMatchStatusDescription,
  };
}
